import { Grid } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PageTitle from 'components/PageTitle/PageTitle';

import TabBar from 'common/components/TabBar/TabBar';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { TestMonitoringText } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setControlTestClicked,
	setTMSelectedTab,
} from 'common/store/reducer/controlTestScriptReducer';
import { formatNumber, scrollToTop } from 'common/utils/utilFunctions';

import styles from 'features/common/Common.module.scss';

import {
	getPendingActionItemsTableData,
	getTestResultsTableData,
	getTestStatus,
} from './api';
import {
	IPendingActionItemTableData,
	ITestMonitoringTestStatusTable,
	ITestResultsTableData,
} from './api/type-def';
import TestMonitoringHeader from './TestMonitoringHeader/TestMonitoringHeader';
import { getIssuesObservationTableData } from './TestMonitoringTab/TestMonitoringDataTable/api';
import { IIssuesObservationTableData } from './TestMonitoringTab/TestMonitoringDataTable/api/type-def';
import IssuesObservationTable from './TestMonitoringTab/TestMonitoringDataTable/IssuesObservationTable/IssuesObservationTable';
import PendingActionItemsTable from './TestMonitoringTab/TestMonitoringDataTable/PendingActionItemsTable/PendingActionItemsTable';
import TestResultsTable from './TestMonitoringTab/TestMonitoringDataTable/TestResults/TestResultsTable';
import TestStatusTable from './TestMonitoringTab/TestMonitoringDataTable/TestStatusTable/TestStatusTable';

interface TabValues {
	name: string;
	count: string | undefined;
}

enum TabsName {
	testStatus = 'Test status',
	results = 'Test results',
	actionItems = 'Pending action items',
	issues = 'Issues/observations',
}

const { OK } = HTTP_STATUS_CODES;

const { testStatus, results, actionItems, issues } = TabsName;

const TestMonitoring = () => {
	const tabIndex = useSelector(
		(state: RootState) => state.controlTestScript.testMonitoringSelectedTab,
	);
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const [testStatusData, setTestStatusData] = useState<
		ITestMonitoringTestStatusTable[]
	>([]);
	const [pendingActionsData, setPendingActionsData] = useState<
		IPendingActionItemTableData[]
	>([]);
	const [processesTableData, setProcessesTableData] = useState<
		ITestResultsTableData[]
	>([]);
	const [issuesObservationTableData, setIssuesObservationTableData] = useState<
		IIssuesObservationTableData[]
	>([]);
	const [tmTabIndex, setTMTabIndex] = useState<number>(tabIndex);
	const tabs = [
		{
			id: 1,
			children: (
				<TestStatusTable testStatusData={testStatusData} key={testStatusData} />
			),
		},
		{
			id: 2,
			children: (
				<TestResultsTable
					processesTableData={processesTableData}
					key={processesTableData}
				/>
			),
		},
		{
			id: 3,
			children: (
				<PendingActionItemsTable
					pendingActionsData={pendingActionsData}
					key={pendingActionsData}
				/>
			),
		},
		{
			id: 4,
			children: (
				<IssuesObservationTable
					issuesObservationData={issuesObservationTableData}
					key={issuesObservationTableData}
				/>
			),
		},
	];
	const fetchTestStatusData = async () => {
		setIsLoading(true);
		const response: any = await getTestStatus();
		if (response?.statusCode === OK) {
			setTestStatusData(response?.responseBody);
			setIsLoading(false);
		}
	};
	const fetchPendingActionTableData = async () => {
		setIsLoading(true);
		const resp: any = await getPendingActionItemsTableData();
		if (resp?.statusCode === OK) {
			setPendingActionsData(resp?.responseBody);
			setIsLoading(false);
		}
	};

	const fetchTestResultsTableData = async () => {
		setIsLoading(true);
		const resp: any = await getTestResultsTableData();
		if (resp?.responseBody) {
			setProcessesTableData(resp.responseBody);
			setIsLoading(false);
		}
	};
	const fetchIssuesObservationTableData = async () => {
		setIsLoading(true);
		const resp: any = await getIssuesObservationTableData();
		if (resp?.responseBody) {
			setIssuesObservationTableData(resp.responseBody);
			setIsLoading(false);
		}
	};

	useEffect(() => scrollToTop(), []);

	useEffect(() => {
		fetchTestStatusData();
		fetchPendingActionTableData();
		fetchTestResultsTableData();
		fetchIssuesObservationTableData();
		dispatch(setControlTestClicked(false));
		setTMTabIndex(tabIndex);
	}, []);

	const onTabSelect = (index: number) => {
		setTMTabIndex(index);
		dispatch(setTMSelectedTab(index));
	};
	const tabDetails: TabValues[] = [
		{
			name: testStatus,
			count: formatNumber(testStatusData.length).toString(),
		},
		{
			name: results,
			count: formatNumber(processesTableData.length).toString(),
		},
		{
			name: actionItems,
			count: formatNumber(pendingActionsData.length).toString(),
		},
		{
			name: issues,
			count: formatNumber(issuesObservationTableData?.length).toString(),
		},
	];

	return (
		<Grid
			className={styles['page-background']}
			data-testid="test-monitoring-testId"
		>
			<PageTitle text={TestMonitoringText} />
			<TestMonitoringHeader
				isLoading={isLoading}
				dateCountData={testStatusData}
			/>
			<div style={{ paddingTop: '50px' }}>
				<TabBar
					activeTab={tmTabIndex}
					details={tabDetails}
					components={tabs}
					showCount
					showButton={false}
					onTabChange={onTabSelect}
				/>
			</div>
		</Grid>
	);
};

export default TestMonitoring;
